// Shared scope picker for admin → integrations forms.
//
// Writes `{scopeType, scopeId}` into the caller's draft state (via
// `draftKey` — the name of the root state slot the page already uses,
// e.g. `_newSupabaseDraft` for the Supabase page). The caller then
// forwards those two fields to `saveIntegration({...draft, scopeType,
// scopeId})`.
//
// Scope modes:
//   - Org — whole organization. scopeId is null. Default for infra kinds
//     (github, google_workspace, npm).
//   - Workspace — a specific workspace in the org. scopeId = workspaceId.
//     Sources from root.workspaces (flat list) → root.jwtClaims.workspaces
//     (fallback). Filtered in real-time by a search input (§I12).
//   - Project — a specific project. scopeId = projectId. Sources from
//     root.projects (canvas-loaded flat list) → root._scopeProjectsByOrg
//     (governance lazy-fetch). Both sources normalised to { id, key, name }.
//
// Usage in a page:
//
//   IntegrationScopePicker: {
//     draftKey: '_newSupabaseDraft',     // required
//     defaultScopeType: 'project'        // from kind.default_scope
//   }

import { apiUrl as _channelApiUrl } from '@symbo.ls/channels'

// ── Helpers ───────────────────────────────────────────────────────────

// Resolve the workspace list from the two possible root sources.
// Priority: root.workspaces (flat array) → jwtClaims.workspaces.
const _getWorkspaces = (root) => {
  const orgId = root?.jwtClaims?.workspace_org_id
  const list = Array.isArray(root?.workspaces)
    ? root.workspaces
    : root?.jwtClaims?.workspaces || []
  return orgId ? list.filter((w) => !w.org_id || w.org_id === orgId) : list
}

// Resolve the project list from the two possible root sources.
// Priority: root.projects (canvas-loaded flat array) →
//           root._scopeProjectsByOrg[orgId] (governance lazy-fetch).
const _getProjects = (root) => {
  const orgId = root?.jwtClaims?.workspace_org_id

  // Canvas context: root.projects is the full flat project list.
  if (Array.isArray(root?.projects) && root.projects.length) {
    return root.projects.map((p) => ({
      id: p.id || p._id || p.key,
      key: p.key || p.slug,
      name: p.name || p.displayName || p.key
    }))
  }

  // Governance context: per-org API cache.
  if (orgId) {
    const cache = (root?._scopeProjectsByOrg || {})[orgId]
    if (Array.isArray(cache)) return cache
  }

  return []
}

// Case-insensitive substring filter. Checks `name` and `key` (key may be
// absent for workspace items — that's fine, the OR short-circuits).
const _filterItems = (items, q) => {
  if (!q) return items
  const lq = q.toLowerCase()
  return items.filter(
    (it) =>
      (it.name || it.label || '').toLowerCase().includes(lq) ||
      (it.key || '').toLowerCase().includes(lq)
  )
}

// ── Lazy project-load (governance context) ───────────────────────────

const _ensureProjectsLoaded = async (root, { force = false } = {}) => {
  if (typeof window === 'undefined') return
  const orgId = root?.jwtClaims?.workspace_org_id
  if (!orgId) {
    _startWatcher(root)
    return
  }
  const cache = root._scopeProjectsByOrg || {}
  if (!force && Array.isArray(cache[orgId])) return

  if (root._scopeProjectsInFlight?.[orgId]) return
  root._scopeProjectsInFlight = { ...(root._scopeProjectsInFlight || {}), [orgId]: true }

  const clearFlight = () => {
    const flight = { ...(root._scopeProjectsInFlight || {}) }
    delete flight[orgId]
    root._scopeProjectsInFlight = flight
  }

  const orgs = root?.jwtClaims?.orgs || []
  const org = orgs.find((o) => (o.id || o._id) === orgId)
  const slug = org?.slug || org?.username
  if (!slug) {
    clearFlight()
    _startWatcher(root)
    return
  }

  // Channel-resolved (single source of truth via @symbo.ls/channels).
  // SYMBOLS_API_URL env override is honored inside the resolver.
  const base = _channelApiUrl()

  const token =
    window.localStorage.getItem('symbols_bridge_access_token') ||
    window.localStorage.getItem('symbols_access_token')
  if (!token) {
    clearFlight()
    root.update({
      _scopeProjectsByOrg: { ...cache, [orgId]: [] },
      _scopeProjectsError: {
        ...(root._scopeProjectsError || {}),
        [orgId]: 'Not signed in'
      }
    })
    return
  }

  let items = []
  let errorMsg = null
  try {
    const url = `${base.replace(/\/+$/, '')}/projects?owner=${encodeURIComponent(slug)}&limit=100`
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    })
    if (!res.ok) {
      errorMsg = res.status === 429 ? 'Rate limited' : `HTTP ${res.status}`
    } else {
      const json = await res.json().catch(() => null)
      items = (json?.data || json?.items || []).map((p) => ({
        id: p.id || p._id,
        name: p.name || p.displayName || p.key,
        key: p.key || p.slug
      }))
    }
  } catch (err) {
    errorMsg = err?.message || 'Fetch failed'
  } finally {
    root.update({
      _scopeProjectsByOrg: {
        ...(root._scopeProjectsByOrg || {}),
        [orgId]: items
      },
      _scopeProjectsError: {
        ...(root._scopeProjectsError || {}),
        [orgId]: errorMsg
      }
    })
    clearFlight()
  }
}

const _startWatcher = (root) => {
  if (typeof window === 'undefined') return
  if (root._scopeProjectsWatcher) return
  const started = Date.now()
  root._scopeProjectsWatcher = setInterval(() => {
    const orgId = root?.jwtClaims?.workspace_org_id
    const orgs = root?.jwtClaims?.orgs || []
    const hasSlug = orgs.some(
      (o) => (o.id || o._id) === orgId && (o.slug || o.username)
    )
    if (orgId && hasSlug) {
      _stopWatcher(root)
      _ensureProjectsLoaded(root)
    } else if (Date.now() - started > 30_000) {
      _stopWatcher(root)
      if (orgId) {
        root.update({
          _scopeProjectsByOrg: {
            ...(root._scopeProjectsByOrg || {}),
            [orgId]: []
          },
          _scopeProjectsError: {
            ...(root._scopeProjectsError || {}),
            [orgId]: 'Session not loaded'
          }
        })
      }
    }
  }, 1000)
}

const _stopWatcher = (root) => {
  if (root?._scopeProjectsWatcher) {
    clearInterval(root._scopeProjectsWatcher)
    root._scopeProjectsWatcher = null
  }
}

// ── Search-list item ────────────────────────────────────────────────────

export const IntegrationScopeListItem = {
  tag: 'button',
  type: 'button',
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: 'Z',
  align: 'center left',
  width: '100%',
  padding: 'V Z1',
  border: 'none',
  background: (el, s) => (s._selected ? 'blue.1' : 'transparent'),
  color: (el, s) => (s._selected ? 'blue' : 'title'),
  cursor: 'pointer',
  fontSize: 'Z2',
  textAlign: 'left',
  ':hover': { background: 'overlayStrong' },

  Label: {
    tag: 'span',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => s._label || ''
  },

  Check: {
    tag: 'span',
    fontSize: 'Y1',
    color: 'blue',
    hide: (el, s) => !s._selected,
    text: '✓'
  },

  // Generic click handler: reads _draftKey, _scopeType, _value from state.
  // Workspace list sets _scopeType:'workspace', project list sets 'project'.
  onClick: (e, el, s) => {
    const dk = s._draftKey
    if (!dk) return
    const type = s._scopeType || 'project'
    const clearKey = type === 'workspace' ? '_scopeWsQuery' : '_scopeProjQuery'
    s.root.update({
      [dk]: {
        ...(s.root?.[dk] || {}),
        scopeType: type,
        scopeId: s._value || null,
        _scopeManualMode: false
      },
      [clearKey]: ''
    })
  }
}

// ── Main export ────────────────────────────────────────────────────────

export const IntegrationScopePicker = {
  flow: 'y',
  gap: 'V',

  onDestroy: (el, s) => _stopWatcher(s?.root),

  Label: {
    tag: 'label',
    fontSize: 'Y2',
    color: 'caption',
    fontWeight: '700',
    text: 'Scope'
  },

  Hint: {
    tag: 'p',
    fontSize: 'Y2',
    color: 'caption',
    margin: '0 0 V 0',
    text: 'Org-wide infra (SSO, code) should stay at org scope. Per-product integrations (databases, analytics) are best scoped to a project so the editor only surfaces them inside that project.'
  },

  Modes: {
    flow: 'x',
    gap: 'Z',
    childExtends: 'IntegrationScopeModeBtn',
    childrenAs: 'state',
    children: (el, s) => {
      const draftKey = el?.parent?.draftKey || s.draftKey
      return ['org', 'workspace', 'project'].map((type) => ({
        type,
        label: type === 'org' ? 'Org' : type === 'workspace' ? 'Workspace' : 'Project',
        draftKey
      }))
    }
  },

  // ── Workspace search-dropdown (§I12) ──────────────────────────────
  WorkspaceDropdown: {
    hide: (el, s) => {
      const draft = s.root?.[s.draftKey] || {}
      return (draft.scopeType || s.defaultScopeType || 'org') !== 'workspace'
    },
    marginTop: 'V',
    flow: 'y',
    gap: 'V',

    // Selected-value badge — shows the chosen workspace name above the list.
    SelectedBadge: {
      tag: 'span',
      padding: 'V Z',
      round: 'Y',
      fontSize: 'Y1',
      fontWeight: '600',
      background: 'blue.1',
      color: 'blue',
      hide: (el, s) => {
        const dk = s.draftKey
        return !dk || !(s.root?.[dk] || {}).scopeId
      },
      text: (el, s) => {
        const dk = s.draftKey
        if (!dk) return ''
        const scopeId = (s.root?.[dk] || {}).scopeId
        if (!scopeId) return ''
        const ws = _getWorkspaces(s.root)
        const found = ws.find(
          (w) => (w.id || w.workspace_id || w._id) === scopeId
        )
        return `Selected: ${found ? (found.name || found.id) : String(scopeId).slice(0, 10) + '…'}`
      }
    },

    SearchInput: {
      tag: 'input',
      type: 'text',
      width: '100%',
      padding: 'V Z1',
      round: 'Y',
      background: 'document',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'line',
      color: 'title',
      fontSize: 'Z2',
      placeholder: 'Search workspaces…',
      value: (el, s) => s.root?._scopeWsQuery || '',
      onInput: (e, el, s) => s.root.update({ _scopeWsQuery: e.target.value })
    },

    List: {
      flow: 'y',
      maxHeight: 'E',
      overflowY: 'auto',
      round: 'Y',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'line',
      background: 'document',

      childExtends: 'IntegrationScopeListItem',
      childrenAs: 'state',
      children: (el, s) => {
        const dk = s.draftKey
        const q = s.root?._scopeWsQuery || ''
        const curId = dk ? (s.root?.[dk] || {}).scopeId || '' : ''
        const ws = _getWorkspaces(s.root).map((w) => ({
          id: w.id || w.workspace_id || w._id,
          name: w.name || w.id || w.workspace_id
        }))
        const filtered = _filterItems(ws, q)
        return filtered.map((it) => ({
          _value: it.id,
          _label: it.name,
          _selected: it.id === curId,
          _draftKey: dk,
          _scopeType: 'workspace'
        }))
      },

      Empty: {
        tag: 'span',
        padding: 'V Z1',
        fontSize: 'Z2',
        color: 'caption',
        fontStyle: 'italic',
        hide: (el, s) => {
          const dk = s.draftKey
          const q = s.root?._scopeWsQuery || ''
          const ws = _getWorkspaces(s.root)
          return _filterItems(ws, q).length > 0
        },
        text: (el, s) => {
          const q = s.root?._scopeWsQuery || ''
          const total = _getWorkspaces(s.root).length
          if (!total) return 'No workspaces in this org.'
          return q ? `No matches for "${q}".` : 'No workspaces found.'
        }
      }
    }
  },

  // ── Project search-dropdown (§I12) ────────────────────────────────
  ProjectDropdown: {
    hide: (el, s) => {
      const draft = s.root?.[s.draftKey] || {}
      return (draft.scopeType || s.defaultScopeType || 'org') !== 'project'
    },
    marginTop: 'V',
    flow: 'y',
    gap: 'V',

    // Trigger API fetch if root.projects is empty (governance context).
    onRender: (el, s) => {
      const draft = s.root?.[s.draftKey] || {}
      const active = (draft.scopeType || s.parent?.defaultScopeType || 'org') === 'project'
      if (active && !(Array.isArray(s.root?.projects) && s.root.projects.length)) {
        _ensureProjectsLoaded(s.root)
      }
    },

    // Selected-value badge.
    SelectedBadge: {
      tag: 'span',
      padding: 'V Z',
      round: 'Y',
      fontSize: 'Y1',
      fontWeight: '600',
      background: 'blue.1',
      color: 'blue',
      hide: (el, s) => {
        const dk = s.draftKey
        return !dk || !(s.root?.[dk] || {}).scopeId
      },
      text: (el, s) => {
        const dk = s.draftKey
        if (!dk) return ''
        const scopeId = (s.root?.[dk] || {}).scopeId
        if (!scopeId) return ''
        const projects = _getProjects(s.root)
        const found = projects.find((p) => (p.id || p.key) === scopeId)
        return `Selected: ${found ? (found.name || found.key) : String(scopeId).slice(0, 10) + '…'}`
      }
    },

    // Loading / error hints.
    LoadingHint: {
      tag: 'span',
      fontSize: 'Y1',
      color: 'caption',
      fontStyle: 'italic',
      hide: (el, s) => {
        if (Array.isArray(s.root?.projects) && s.root.projects.length) return true
        const orgId = s.root?.jwtClaims?.workspace_org_id
        return orgId && Array.isArray((s.root?._scopeProjectsByOrg || {})[orgId])
      },
      text: 'Loading projects…'
    },

    ErrorHint: {
      tag: 'span',
      fontSize: 'Y1',
      color: 'red',
      hide: (el, s) => {
        const orgId = s.root?.jwtClaims?.workspace_org_id
        return !orgId || !(s.root?._scopeProjectsError || {})[orgId]
      },
      text: (el, s) => {
        const orgId = s.root?.jwtClaims?.workspace_org_id
        const err = orgId ? (s.root?._scopeProjectsError || {})[orgId] : null
        return err ? `Could not load projects: ${err}` : ''
      }
    },

    SearchInput: {
      tag: 'input',
      type: 'text',
      width: '100%',
      padding: 'V Z1',
      round: 'Y',
      background: 'document',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'line',
      color: 'title',
      fontSize: 'Z2',
      placeholder: 'Search projects…',
      value: (el, s) => s.root?._scopeProjQuery || '',
      onInput: (e, el, s) => s.root.update({ _scopeProjQuery: e.target.value })
    },

    List: {
      flow: 'y',
      maxHeight: 'E',
      overflowY: 'auto',
      round: 'Y',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'line',
      background: 'document',

      childExtends: 'IntegrationScopeListItem',
      childrenAs: 'state',
      children: (el, s) => {
        const dk = s.draftKey
        const q = s.root?._scopeProjQuery || ''
        const curId = dk ? (s.root?.[dk] || {}).scopeId || '' : ''
        const projects = _getProjects(s.root)
        const filtered = _filterItems(projects, q)
        return filtered.map((p) => ({
          _value: p.id || p.key,
          _label: p.name || p.key || p.id,
          _selected: (p.id || p.key) === curId,
          _draftKey: dk,
          _scopeType: 'project'
        }))
      },

      Empty: {
        tag: 'span',
        padding: 'V Z1',
        fontSize: 'Z2',
        color: 'caption',
        fontStyle: 'italic',
        hide: (el, s) => {
          const q = s.root?._scopeProjQuery || ''
          const projects = _getProjects(s.root)
          // Hide empty message while loading.
          if (
            !(Array.isArray(s.root?.projects) && s.root.projects.length) &&
            !(Array.isArray((s.root?._scopeProjectsByOrg || {})[s.root?.jwtClaims?.workspace_org_id]))
          ) {
            return true
          }
          return _filterItems(projects, q).length > 0
        },
        text: (el, s) => {
          const q = s.root?._scopeProjQuery || ''
          const total = _getProjects(s.root).length
          if (!total) return 'No projects in this org.'
          return q ? `No matches for "${q}".` : 'No projects found.'
        }
      }
    },

    // Toggle between list and manual-ID entry.
    ManualEntryToggle: {
      tag: 'button',
      type: 'button',
      padding: 'V Z',
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      fontSize: 'Y1',
      color: 'caption',
      textDecoration: 'underline',
      textAlign: 'left',
      text: (el, s) => {
        const dk = s.draftKey
        return (s.root?.[dk] || {})._scopeManualMode
          ? '← Back to list'
          : '+ Enter project ID manually'
      },
      onClick: (e, el, s) => {
        const dk = s.draftKey
        if (!dk) return
        const current = s.root?.[dk] || {}
        s.root.update({
          [dk]: { ...current, _scopeManualMode: !current._scopeManualMode }
        })
      }
    },

    ManualEntryRow: {
      hide: (el, s) => {
        const dk = s.draftKey
        return !dk || !(s.root?.[dk] || {})._scopeManualMode
      },
      flow: 'y',
      gap: 'V',
      Label: {
        tag: 'label',
        fontSize: 'Y1',
        color: 'caption',
        text: 'Project ID'
      },
      Input: {
        tag: 'input',
        type: 'text',
        width: '100%',
        padding: 'Z',
        round: 'Y',
        background: 'document',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'line',
        color: 'title',
        fontSize: 'Z2',
        placeholder: 'e.g. prj_abc123',
        value: (el, s) => (s.root?.[s.draftKey] || {}).scopeId || '',
        onInput: (e, el, s) => {
          const dk = s.draftKey
          if (!dk) return
          s.root.update({
            [dk]: {
              ...(s.root?.[dk] || {}),
              scopeType: 'project',
              scopeId: e.target.value || null,
              _scopeManualMode: true
            }
          })
        }
      }
    }
  }
}

export const IntegrationScopeOption = {
  tag: 'option',
  text: (el, s) => s.label || '',
  onCreate: (el, s) => {
    if (el.node) el.node.value = s.value == null ? '' : s.value
  },
  onUpdate: (el, s) => {
    if (el.node) el.node.value = s.value == null ? '' : s.value
  }
}

export const IntegrationScopeModeBtn = {
  tag: 'button',
  type: 'button',
  padding: 'V Z',
  round: 'Y',
  borderWidth: '1px',
  borderStyle: 'solid',
  fontSize: 'Y2',
  fontWeight: '700',
  cursor: 'pointer',
  background: (el, s) => {
    const parent = s.parent || {}
    const draft = s.root?.[s.draftKey] || {}
    const active = (draft.scopeType || parent.defaultScopeType || 'org') === s.type
    return active ? 'blue' : 'transparent'
  },
  color: (el, s) => {
    const parent = s.parent || {}
    const draft = s.root?.[s.draftKey] || {}
    const active = (draft.scopeType || parent.defaultScopeType || 'org') === s.type
    return active ? 'white' : 'title'
  },
  borderColor: 'line',
  text: (el, s) => s.label,
  onClick: (e, el, s) => {
    s.root.update({
      [s.draftKey]: {
        ...(s.root?.[s.draftKey] || {}),
        scopeType: s.type,
        scopeId: s.type === 'org' ? null : (s.root?.[s.draftKey] || {}).scopeId || null
      },
      // Clear search queries on mode switch.
      _scopeWsQuery: '',
      _scopeProjQuery: ''
    })
  }
}

// Read `{scopeType, scopeId}` off a draft, supplying defaults. Use this
// in page save handlers so you don't repeat the fallback logic.
export const resolveScope = (draft, defaultScopeType = 'org') => {
  const scopeType = draft?.scopeType || defaultScopeType
  if (scopeType === 'org') return { scopeType: 'org', scopeId: null }
  return { scopeType, scopeId: draft?.scopeId || null }
}

// Tiny scope badge for instance cards. Reads `s.scope_type` / `s.scope_id`.
export const IntegrationScopeBadge = {
  tag: 'span',
  padding: 'V Z',
  round: 'Y',
  fontSize: 'Y1',
  fontWeight: '700',
  background: 'overlayStrong',
  color: 'caption',
  text: (el, s) => {
    if (!s.scope_type || s.scope_type === 'org') return 'Org'
    const label = s.scope_type === 'workspace' ? 'Workspace' : 'Project'
    const short = s.scope_id ? `: ${String(s.scope_id).slice(0, 6)}…` : ''
    return `${label}${short}`
  }
}
